import { externalLinks, links } from 'constants'
import { IS_WEB } from 'constants/platform'
import React, { useState } from 'react'
import { StyleSheet } from 'react-native'
import { v4 as uuidv4 } from 'uuid'

import { MembershipModal } from 'partials/MembershipModal'
import { SubscriptionModal } from 'partials/SubscriptionModal'

import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { Image } from 'widgets/Image'
import { Pressable } from 'widgets/Pressable'
import { PressableText } from 'widgets/PressableText'

import { usePendingMembership } from 'hooks/usePendingMembership'
import { useSubscribed } from 'hooks/useSubscribed'
import { useSubscription } from 'hooks/useSubscription'
import { useUser } from 'hooks/useUser'

import { defaultPostThumbnailUrl } from 'utils/media'
import { handleShareAction } from 'utils/sharing'
import commonStyles from 'utils/styles/commonStyles'

import { useFocusMode } from 'contexts/FocusModeContext'

import { Program } from 'types/program'
import { UserType } from 'types/user'

type MembershipRowProps = {
  mode: 'pending' | 'active'
  program: Program
  navigation: any
  currentUser: UserType
  isLegacySubscription?: boolean
}

const styles = StyleSheet.create({
  titleContainer: { width: '50%' },
})

export const MembershipRow = ({
  mode,
  program,
  navigation,
  currentUser,
  isLegacySubscription = false,
}: MembershipRowProps) => {
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false)
  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false)

  const { data: trainer } = useUser({ userId: program.trainer.id })

  const { cancelPendingMembership } = usePendingMembership({
    userId: currentUser?.id,
    programId: program?.id,
  })

  const { cancelSubscription } = useSubscription(currentUser?.id, program?.id)

  const handleCancelPendingMembership = () => cancelPendingMembership.mutateAsync()
  const handleCancelSubscription = () => cancelSubscription.mutateAsync()

  const handleUpspaceShare = async () => {
    const url = `${externalLinks.UPSPACE_FULL_PATH}/group/${trainer?.username}`

    await handleShareAction(
      url,
      `Check out this group on Upspace!`,
      navigation,
      links.SHARE_GROUP_PAGE,
      trainer?.username,
      links.GROUP_STACK,
    )
  }

  const focusMode = useFocusMode()
  const { isSubscribed } = useSubscribed({ programId: program?.id })

  const navigateToUpspace = () => {
    if (isSubscribed) {
      focusMode.setGroup(program)
      return
    }
    navigation.navigate(links.GROUP_STACK, {
      screen: links.GROUP_PAGE,
      params: { username: trainer?.username },
    })
  }

  const navigateToProfile = () => {
    navigation.navigate(links.PROFILE_STACK, {
      screen: links.MEMBER_PROFILE_PAGE,
      params: { username: trainer?.username },
    })
  }

  return (
    <Box
      testID={`MembershipRow-${program?.id}`}
      flexDirection='row'
      justifyContent='space-between'
      margin={{ right: 'lg' }}
    >
      <SubscriptionModal
        trainer={program?.trainer}
        isSubscribed={mode === 'active'}
        open={isSubscriptionModalOpen}
        handleClose={() => setIsSubscriptionModalOpen(false)}
      />

      {currentUser?.id !== undefined && (
        <MembershipModal
          open={isMembershipModalOpen}
          mode={mode}
          handleClose={() => setIsMembershipModalOpen(false)}
          handleYes={mode === 'pending' ? handleCancelPendingMembership : handleCancelSubscription}
        />
      )}

      <Box padding='md' align='center'>
        <Pressable onPress={navigateToUpspace}>
          <Box width={64} height={64} borderRadius='sm' style={commonStyles.overflowHidden}>
            <Image
              cacheKey={
                program
                  ? // eslint-disable-next-line no-underscore-dangle
                    `${program?.id}-program-cover-${program?.cover?._original?.etag}-small`
                  : uuidv4()
              }
              imageWidth={160}
              style={commonStyles.fullSize}
              source={program?.cover?.url || defaultPostThumbnailUrl}
            />
          </Box>
        </Pressable>
      </Box>

      <Box flexDirection='column' margin={{ top: 'lg' }} style={styles.titleContainer}>
        <Box flexDirection='row' align='center'>
          <Box margin={{ right: 'xs' }}>
            <PressableText bold fontSize='sm' numberOfLines={1} onPress={navigateToUpspace}>
              {program?.title}
            </PressableText>
          </Box>
        </Box>
        <PressableText
          numberOfLines={2}
          color='greyDarkest'
          fontSize='sm'
          onPress={navigateToProfile}
        >
          @{trainer?.username}
        </PressableText>
      </Box>

      <Box margin={{ top: 'xl' }} flexDirection='row' justifyContent='space-between'>
        <Pressable testID='ShareIcon' onPress={handleUpspaceShare}>
          <Icon
            name='share'
            size={20}
            color='black'
            padding={{ right: IS_WEB ? 'sm' : undefined }}
          />
        </Pressable>

        {mode === 'active' && !isLegacySubscription && (
          <Box margin={{ left: 'smd' }}>
            <Pressable
              testID='RemoveActiveMembershipButton'
              onPress={() => setIsMembershipModalOpen(true)}
            >
              <Icon name='x' size={20} color='black' />
            </Pressable>
          </Box>
        )}

        {mode === 'pending' && (
          <Box margin={{ left: 'smd' }}>
            <Pressable
              testID='RemovePendingMembershipButton'
              onPress={() => setIsMembershipModalOpen(true)}
            >
              <Icon name='x' size={20} color='black' />
            </Pressable>
          </Box>
        )}
      </Box>
    </Box>
  )
}
